import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useAuth } from './auth'
import { PRODUCT_NAME, usePlatform } from './platform'
import { LanguageSwitcher, useT } from './i18n'
import nav from './i18n/nav'
import { Icon, Logo } from './lib/ui'
import { SignIn } from './pages/SignIn'
import { Register } from './pages/Register'
import { Dashboard } from './pages/Dashboard'
import { Courses } from './pages/Courses'
import { Course } from './pages/Course'
import { Lesson } from './pages/Lesson'
import { Leaderboard } from './pages/Leaderboard'
import { Settings } from './pages/Settings'
import { ChoosePassword } from './pages/ChoosePassword'

export function App() {
  const { user, mustChangePassword, ready } = useAuth()

  // Deciding what to render before /me has answered would flash the login
  // screen at someone who is already signed in.
  if (!ready) return <Splash />

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<SignIn />} />
        <Route path="/register" element={<Register />} />
        {/* Anything else, signed out, means the login screen — but /register has
            to stay reachable, which is why it is named before the catch-all. */}
        <Route path="*" element={<ToLogin />} />
      </Routes>
    )
  }

  // Signed in with a password somebody else chose (must_change_password): the
  // server refuses everything but choosing their own, so that is all there is.
  // No route is matched and none is changed, so once it is chosen the site
  // carries on from the address the learner is at.
  if (mustChangePassword) return <ChoosePassword />

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:courseId" element={<Course />} />
        <Route path="/lessons/:lectureId" element={<Lesson />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/settings" element={<Settings />} />
        {/* The doors, once through them, lead to where the learner was going. */}
        <Route path="/login" element={<Onwards />} />
        <Route path="/register" element={<Onwards />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  )
}

/** Where a signed-out visit was headed, carried through the login screen. */
type Headed = { from?: Pick<Location, 'pathname' | 'search' | 'hash'> }

/**
 * Sends a signed-out visitor to the login screen, remembering the page they
 * asked for — a lesson link from a group chat, say — so that signing in (and
 * choosing a password, if they must) ends up there rather than at home.
 */
function ToLogin() {
  const location = useLocation()
  const from = { pathname: location.pathname, search: location.search, hash: location.hash }
  return <Navigate to="/login" replace state={{ from } satisfies Headed} />
}

/** After signing in or registering: on to the page that was asked for, or home. */
function Onwards() {
  const { state } = useLocation()
  const from = (state as Headed | null)?.from
  // Only a page of this site's own, and never one of the doors again. The
  // state is this app's own (ToLogin), never read from an address, but a path
  // is still checked before it is followed.
  const path = from?.pathname ?? ''
  const ok = from !== undefined && /^\/(?!\/)/.test(path) && !/^\/(login|register)(\/|$)/.test(path)
  return <Navigate to={ok ? `${path}${from.search}${from.hash}` : '/'} replace />
}

function Shell({ children }: { children: ReactNode }) {
  const t = useT(nav)
  const { user, signOut } = useAuth()
  const platform = usePlatform()
  const { pathname } = useLocation()

  // The lesson player wants the width; the chrome would only crowd it.
  const bare = pathname.startsWith('/lessons/')

  return (
    <div className={bare ? 'shell shell--bare' : 'shell'}>
      <a className="skip-link" href="#content">
        {t('skipToContent')}
      </a>
      <aside className="sidebar">
        <div className="brand">
          {platform.logo_path ? (
            <img src={platform.logo_path} alt={platform.name} />
          ) : platform.name === PRODUCT_NAME ? (
            <Logo />
          ) : (
            <span className="brand__name">{platform.name}</span>
          )}
        </div>
        <nav aria-label={t('mainNavigation')}>
          <NavLink to="/" end>
            <Icon name="home" />
            <span>{t('home')}</span>
          </NavLink>
          <NavLink to="/courses">
            <Icon name="courses" />
            <span>{t('myCourses')}</span>
          </NavLink>
          <NavLink to="/leaderboard">
            <Icon name="leaderboard" />
            <span>{t('leaderboard')}</span>
          </NavLink>
          <NavLink to="/settings">
            <Icon name="settings" />
            <span>{t('settings')}</span>
          </NavLink>
        </nav>
        <div className="sidebar__foot">
          <span className="sidebar__who" title={t('signedInAs', { name: user?.full_name || user?.phone || '' })}>
            {user?.full_name || user?.phone}
          </span>
          <div className="sidebar__actions">
            <LanguageSwitcher />
            <button type="button" className="link sidebar__signout" onClick={() => void signOut()}>
              <Icon name="logout" size={18} />
              <span>{t('signOut')}</span>
            </button>
          </div>
        </div>
      </aside>
      <main id="content" className="content" tabIndex={-1}>
        {children}
      </main>
    </div>
  )
}

function Splash() {
  const t = useT(nav)
  return (
    <div className="splash" role="status" aria-live="polite" aria-label={t('loading')}>
      <div className="splash__pulse" />
    </div>
  )
}

function NotFound() {
  const t = useT(nav)
  return (
    <div className="empty">
      <h1>{t('notFoundTitle')}</h1>
      <p>{t('notFoundBody')}</p>
      <NavLink className="button" to="/courses">
        <Icon name="courses" size={18} />
        {t('backToCourses')}
      </NavLink>
    </div>
  )
}
